import { Alert, Button } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStations } from '@/features/cargadores/hooks/useStations';
import { StationStatusBadge, StationSyncBadge } from '@/features/cargadores/components/StationBadges';

/**
 * Tope de cargadores traídos para el panel de la locación. ponytail: sin paginación; el
 * panel scrollea. Si una ubicación supera los 100 cargadores, subir el límite o paginar.
 */
const STATIONS_LIMIT = 100;

/**
 * Panel de cargadores instalados en una ubicación, anclado al mapa del detalle.
 *
 * **Solo se monta para roles con permiso de listar cargadores** (el gate vive en la página):
 * `useStations` pega contra `GET /admin/stations`, que es solo `SUPER_ADMIN`. Así un rol de
 * lectura de ubicaciones (SOPORTE_N3 / MARKETING) nunca dispara la request ni ve el 403.
 *
 * Filtra por `locationId` (el contrato ya lo soporta); la identidad de cada fila es el
 * ChargePoint ID y navega al detalle del cargador.
 */
export function LocationStationsPanel({ locationId }: { locationId: string }) {
  const { data, meta, loading, error, refresh } = useStations({
    limit: STATIONS_LIMIT,
    initialFilters: { locationId },
  });

  const count = meta.total;

  return (
    <div className="locaciones-map__stations" aria-label="Cargadores instalados en esta ubicación">
      <div className="locaciones-map__stations-head">
        <ThunderboltOutlined aria-hidden />
        <span className="locaciones-map__stations-count">
          {loading ? 'Cargadores' : `${count} ${count === 1 ? 'cargador' : 'cargadores'}`}
        </span>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los cargadores"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : loading ? (
        <p className="locaciones-map__stations-empty">Cargando cargadores…</p>
      ) : data.length === 0 ? (
        <p className="locaciones-map__stations-empty">
          Todavía no hay cargadores instalados acá.
        </p>
      ) : (
        <ul className="locaciones-map__stations-list">
          {data.map((station) => (
            <li key={station.id}>
              <Link
                className="locaciones-map__stations-item"
                to={`/cargadores/${encodeURIComponent(station.id)}`}
              >
                <span className="locaciones-map__stations-id" title={station.id}>
                  {station.id}
                </span>
                <span className="locaciones-map__stations-badges">
                  {/* El panel usa la superficie del tema (`--ge-color-surface`), la misma
                      sobre la que los tokens de estado están calibrados: los badges van
                      con su estilo normal (label + borde con el color de estado). */}
                  <StationStatusBadge status={station.status} />
                  <StationSyncBadge status={station.syncStatus} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
